import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsers, useActiveGame } from '@/hooks/useShared';
import type { Game, GamePlayer } from '@/lib/types';
import { addBuyIn, endGame, markPlayerLeftEarly, rejoinPlayer } from '@/hooks/useGamePlay';
import { addPlayer, deleteGame } from '@/hooks/useGameSetup';
import { useAuth } from '@/hooks/useAuth';
import { canManageUsers } from '@/lib/roles';
import { AddPlayerModal } from '@/components/setup/AddPlayerModal';
import { LeaveEarlyModal } from '@/components/game/LeaveEarlyModal';
import { Modal } from '@/components/ui';
import { formatCurrency } from '@/lib/utils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const triggerHaptic = (type: 'press' | 'success') => {
  if (typeof window !== 'undefined' && window.navigator?.vibrate) {
    if (type === 'press') {
      window.navigator.vibrate(30);
    } else if (type === 'success') {
      window.navigator.vibrate([60, 40, 60]);
    }
  }
};

function timestampToMillis(timestamp: unknown): number {
  if (!timestamp) return 0;
  if (typeof timestamp === 'number') return Number.isFinite(timestamp) ? timestamp : 0;
  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (timestamp instanceof Date) return timestamp.getTime();

  if (typeof timestamp === 'object') {
    const timestampWithMillis = timestamp as { toMillis?: unknown };
    if (typeof timestampWithMillis.toMillis === 'function') {
      const millis = timestampWithMillis.toMillis();
      return typeof millis === 'number' && Number.isFinite(millis) ? millis : 0;
    }

    const serializedTimestamp = timestamp as { seconds?: unknown; nanoseconds?: unknown };
    if (typeof serializedTimestamp.seconds === 'number') {
      const nanos = typeof serializedTimestamp.nanoseconds === 'number'
        ? serializedTimestamp.nanoseconds
        : 0;
      return (serializedTimestamp.seconds * 1000) + Math.floor(nanos / 1_000_000);
    }
  }

  return 0;
}

const PAYMENT_METHODS_KEY = (gameId: string) => `pk_payment_methods_${gameId}`;

function loadPaymentMethods(gameId: string): Record<string, 'digital' | 'cash'> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(PAYMENT_METHODS_KEY(gameId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePaymentMethods(gameId: string, methods: Record<string, 'digital' | 'cash'>): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PAYMENT_METHODS_KEY(gameId), JSON.stringify(methods));
  } catch {
    // ignore
  }
}

export function ActiveGamePage({ game: propGame }: { game?: Game | null }) {
  const { activeGame: hookGame, loading: hookLoading } = useActiveGame();
  const game = propGame || hookGame;
  const loading = propGame ? false : hookLoading;
  const navigate = useNavigate();
  const { users } = useUsers();
  const { user } = useAuth();
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [ending, setEnding] = useState(false);
  const [openCustomInput, setOpenCustomInput] = useState<string | null>(null);
  const [customAmounts, setCustomAmounts] = useState<Record<string, string>>({});
  const [inputPolarity, setInputPolarity] = useState<Record<string, number>>({});
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [pendingBuyIns, setPendingBuyIns] = useState<Set<string>>(new Set());
  const [successBuyIns, setSuccessBuyIns] = useState<Set<string>>(new Set());
  const [flashStage, setFlashStage] = useState<'idle' | 'white' | 'green'>('idle');
  const [paymentMethods, setPaymentMethods] = useState<Record<string, 'digital' | 'cash'>>(() =>
    game ? loadPaymentMethods(game.id) : {}
  );
  const debounceRef = useRef<Record<string, number>>({});
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [selectedLeavePlayer, setSelectedLeavePlayer] = useState<GamePlayer | null>(null);
  const [leaveModalMode, setLeaveModalMode] = useState<'leave' | 'edit'>('leave');

  // Reconcile payment methods: apply defaults only for players not in sessionStorage.
  React.useEffect(() => {
    if (!game) return;

    setPaymentMethods(prev => {
      const next = { ...prev };
      let changed = false;
      const persisted = loadPaymentMethods(game.id);
      const playerIds = new Set(game.players.map(player => player.user_id));

      // Remove players who are no longer in the game.
      Object.keys(next).forEach(userId => {
        if (!playerIds.has(userId)) {
          delete next[userId];
          changed = true;
        }
      });

      game.players.forEach(player => {
        // If already persisted from a prior session, never override.
        if (player.user_id in persisted) {
          const persistedMethod = persisted[player.user_id];
          if (persistedMethod && next[player.user_id] !== persistedMethod) {
            next[player.user_id] = persistedMethod;
            changed = true;
          }
          return;
        }

        // Not persisted yet — apply default from profile or embedded data.
        const playerProfile = users.find(candidate => candidate.id === player.user_id);
        const embeddedMethod = player.default_buyin_method === 'cash' ? 'cash' : null;
        const desiredMethod = playerProfile?.default_buyin_method === 'cash' || (!playerProfile && embeddedMethod === 'cash')
          ? 'cash'
          : 'digital';

        if (next[player.user_id] !== desiredMethod) {
          next[player.user_id] = desiredMethod;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [users, game]);

  // Clean up storage for removed players.
  React.useEffect(() => {
    if (!game) return;
    const playerIds = new Set(game.players.map(p => p.user_id));
    const persisted = loadPaymentMethods(game.id);
    const cleaned: Record<string, 'digital' | 'cash'> = {};
    let changed = false;
    Object.entries(persisted).forEach(([userId, method]) => {
      if (playerIds.has(userId)) {
        cleaned[userId] = method;
      } else {
        changed = true;
      }
    });
    if (changed) {
      savePaymentMethods(game.id, cleaned);
    }
  }, [game]);

  if (loading) {
    return (
      <div className="bg-[#f5f7f8] min-h-screen pb-[180px] flex items-center justify-center">
        <div style={{ color: '#555', fontSize: 14 }}>Loading Session...</div>
      </div>
    );
  }

  if (!game || game.status !== 'active') {
    return (
      <div className="bg-[#f5f7f8] min-h-screen pb-[180px] flex flex-col items-center justify-center">
        <div style={{ color: '#555', fontSize: 14, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🃏</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#2c2f30', marginBottom: 8 }}>No Active Game</div>
          <div style={{ color: '#595c5d', marginBottom: 24 }}>Go to Setup to create a new session.</div>
          <button
            onClick={() => navigate('/setup')}
            style={{
              backgroundColor: '#006855',
              color: '#fff',
              border: 'none',
              borderRadius: 24,
              padding: '12px 24px',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Go to Setup
          </button>
        </div>
      </div>
    );
  }

  const isAdmin = canManageUsers(user);
  const activePlayers = game.players.filter(p => !p.is_spectator);
  const spectators = game.players.filter(p => p.is_spectator);

  const buyInHistory = game.players
    .flatMap(p => {
      const playerUser = users.find(u => u.id === p.user_id);
      const displayName = playerUser?.name || p.name || 'Player';
      return (p.buy_ins || []).map(b => ({
        ...b,
        playerName: displayName,
        userId: p.user_id
      }));
    })
    .sort((a, b) => timestampToMillis(b.timestamp) - timestampToMillis(a.timestamp));

  const recentHistory = buyInHistory.slice(0, 5);

  const handleBuyIn = async (playerUserId: string, amount: number) => {
    const last = debounceRef.current[playerUserId];
    if (last && Date.now() - last < 500) return;
    debounceRef.current[playerUserId] = Date.now();

    triggerHaptic('press');
    setPendingBuyIns(prev => new Set(prev).add(playerUserId));

    try {
      const method = paymentMethods[playerUserId] || 'digital';
      await addBuyIn(game.id, playerUserId, amount, method, user?.id ?? '');
      
      triggerHaptic('success');
      setPendingBuyIns(prev => {
        const next = new Set(prev);
        next.delete(playerUserId);
        return next;
      });
      setSuccessBuyIns(prev => new Set(prev).add(playerUserId));
      
       // Trigger success flash
       setFlashStage('white');
       setTimeout(() => setFlashStage('green'), 50);
       setTimeout(() => setFlashStage('idle'), 400);
      
      setTimeout(() => {
        setSuccessBuyIns(prev => {
          const next = new Set(prev);
          next.delete(playerUserId);
          return next;
        });
      }, 800);

    } catch (err) {
      console.error('Buy-in failed:', err);
      setPendingBuyIns(prev => {
        const next = new Set(prev);
        next.delete(playerUserId);
        return next;
      });
    }
  };

  const togglePolarity = (userId: string) => {
    setInputPolarity(prev => ({
      ...prev,
      [userId]: (prev[userId] || 1) * -1
    }));
  };

  const handleCustomSubmit = (playerUserId: string) => {
    const amount = parseFloat(customAmounts[playerUserId] || '');
    const polarity = inputPolarity[playerUserId] || 1;
    if (!isNaN(amount) && amount !== 0) {
      handleBuyIn(playerUserId, amount * polarity);
      setOpenCustomInput(null);
      setCustomAmounts(prev => ({ ...prev, [playerUserId]: '' }));
    }
  };

  const handleEndGame = async () => {
    setEnding(true);
    try {
      await endGame(game.id);
      navigate(`/settlement/${game.id}`);
    } finally {
      setEnding(false);
      setShowEndConfirm(false);
    }
  };

  const handleDeleteActiveGame = async () => {
    if (!confirm('Are you sure you want to DELETE this active game? This will remove it from the active session.')) return;
    try {
      await deleteGame(game.id, user?.id ?? '');
      navigate('/library');
    } catch (err) {
      alert('Failed to delete game');
    }
  };

  const handleRemovePlayer = async (userId: string) => {
    if (!game || !confirm('Remove this player from the game?')) return;
    const newPlayers = game.players.filter(p => p.user_id !== userId);
    await updateDoc(doc(db, 'games', game.id), { players: newPlayers });
  };

  const handleAddPlayer = async (playerData: { user_id: string; name: string; email?: string; default_buyin_method?: 'digital' | 'cash' }) => {
    if (!game) return;
    await addPlayer(game.id, playerData);
  };

    return (
      <div className="bg-[#f5f7f8] min-h-screen pb-[180px]">
        {/* Success Flash Overlay */}
         <div style={{
           position: 'fixed',
           top: 0,
           left: 0,
           right: 0,
           bottom: 0,
           pointerEvents: 'none',
           zIndex: 9999,
           transition: 'all 0.2s ease-out',
           opacity: flashStage === 'idle' ? 0 : 1,
           backgroundColor: flashStage === 'white' ? 'rgba(255, 255, 255, 0.6)' : flashStage === 'green' ? 'rgba(0, 104, 85, 0.15)' : 'transparent',
           boxShadow: flashStage === 'green' ? 'inset 0 0 40px 10px rgba(0, 104, 85, 0.3)' : 'none',
         }} />
        
        <div className="max-w-[400px] mx-auto pt-4">

        {/* Header */}

        {/* Pot Hero */}
        <div className="bg-[#e8f5f0] border border-[#0068551f] rounded-2xl p-[12px_18px] mb-2">
          <div className="flex justify-between items-start">
            <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-[#006855] opacity-70 mb-1">Total Pot</div>
            {isAdmin && (
              <button
                onClick={() => setShowEndConfirm(true)}
                className="bg-[#006855] text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-lg cursor-pointer border-none shadow-sm"
              >
                End Game
              </button>
            )}
          </div>
          <div className="flex items-baseline gap-2 mb-1.5 flex-wrap">
            <span className="text-[22px] font-extrabold text-[#2c2f30]">
              {formatCurrency(game.players.reduce((sum, p) => sum + (p.total_bought_in || 0), 0))}
            </span>
            <span className="text-[13px] text-[#595c5d] font-medium">
              (cash: {formatCurrency(game.players.reduce((sum, p) => sum + (p.cash_in || 0), 0))})
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex gap-4 text-xs text-[#006855] opacity-90">
              <div className="flex gap-1">
                <span className="font-medium opacity-80">Players:</span>
                <span className="font-black">{activePlayers.length}</span>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-3">
                <button onClick={() => setShowAddPlayerModal(true)} className="text-[#006855] text-[11px] font-bold uppercase tracking-wide cursor-pointer p-0 underline">
                  + Add Player
                </button>
                {game.players.every(p => (p.total_bought_in || 0) === 0) && (
                  <button
                    onClick={async () => {
                      if (!confirm('Add initial $20 buy-in to all players?')) return;
                      for (const p of activePlayers) {
                        await handleBuyIn(p.user_id, 20);
                      }
                    }}
                    className="text-[#006855] text-[11px] font-bold uppercase tracking-wide cursor-pointer p-0 underline"
                  >
                    + $20 All Players
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Activity Feed */}
        <div className="mb-5 p-3 bg-white rounded-xl border border-[#e5e9ea] max-h-[120px] overflow-y-auto">
          <div className="text-[10px] font-bold uppercase text-[#595c5d] mb-2 tracking-wider">
            Recent Activity
          </div>
          {recentHistory.length === 0 ? (
            <div className="text-[11px] text-[#bbb] italic">No buy-ins yet</div>
          ) : (
            <div className="flex flex-col gap-1">
              {recentHistory.map((entry, i) => (
                <div key={i} className="text-xs text-[#595c5d] flex justify-between items-center">
                  <span className="font-semibold text-[#2c2f30]">{entry.playerName}</span>
                  <span className="flex items-center gap-1">
                    <span className="font-bold">+{formatCurrency(entry.amount)}</span>
                    <span className="text-[10px] opacity-60">{entry.type === 'cash' ? '💵' : '📱'}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Players Section */}
        <div className="mb-3 mt-3">
          <div className="text-[12px] font-bold uppercase tracking-wider text-[#595c5d]">Players</div>
        </div>

        {activePlayers.map((player) => {
          const playerUser = users.find(u => u.id === player.user_id);
           const playerName = playerUser?.name || player.name || 'Player';
           const initial = playerName.charAt(0).toUpperCase();
           const isOpen = openCustomInput === player.user_id;
           const isPending = pendingBuyIns.has(player.user_id);
           const isSuccess = successBuyIns.has(player.user_id);


          return (
            <div key={player.user_id}>
              {player.left_early ? (
                <div className="bg-white rounded-xl border border-[#e5e9ea] p-[12px_14px] mb-1.5 flex items-center opacity-60">
                  <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center mr-3 text-[15px] font-extrabold text-white shrink-0">{initial}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#2c2f30]">{playerName}</span>
                      <span className="text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                    </div>
                    <div className="text-[11px] text-[#595c5d] mt-0.25">
                      Stack: {formatCurrency(player.left_stack || 0)} / Cash: {formatCurrency(player.left_cash || 0)}
                      <br />
                      Buy-in: {formatCurrency(player.total_bought_in || 0)}
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 ml-auto">
                      <button
                        className="bg-[#f5f7f8] border border-[#e5e9ea] rounded-lg px-2 py-1 text-[10px] font-bold uppercase text-[#595c5d] cursor-pointer"
                        onClick={() => {
                          setSelectedLeavePlayer(player);
                          setLeaveModalMode('edit');
                          setLeaveModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="bg-[#e8f5f0] border border-[#e5e9ea] rounded-lg px-2 py-1 text-[10px] font-bold uppercase text-[#006855] cursor-pointer"
                        onClick={async () => {
                          if (!confirm('Rejoin this player to the game?')) return;
                          await rejoinPlayer(game.id, user?.id ?? '', player.user_id);
                        }}
                      >
                        Rejoin
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-[#e5e9ea] p-[12px_14px] mb-1.5 flex items-center relative overflow-hidden">
                    {isAdmin && (player.total_bought_in || 0) === 0 && (
                      <button
                        className="absolute top-1.5 right-2 text-[#595c5d] text-lg cursor-pointer opacity-40 hover:opacity-100"
                        onClick={() => handleRemovePlayer(player.user_id)}
                        aria-label="Remove player"
                      >
                        ×
                      </button>
                    )}
                    {isAdmin && !player.left_early && (player.total_bought_in || 0) > 0 && (
                      <button
                        className="absolute top-1.5 right-2 text-[#595c5d] text-[10px] font-bold uppercase cursor-pointer opacity-40 hover:opacity-100"
                        onClick={() => {
                          setSelectedLeavePlayer(player);
                          setLeaveModalMode('leave');
                          setLeaveModalOpen(true);
                        }}
                        aria-label="Player left early"
                      >
                        Leave
                      </button>
                    )}
                    <div className="w-9 h-9 rounded-full bg-[#006855] flex items-center justify-center mr-3 text-[15px] font-extrabold text-white shrink-0">{initial}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#2c2f30]">{playerName}</div>
                      <div className="text-[11px] text-[#595c5d] mt-0.25">
                        Buy-in: {formatCurrency(player.total_bought_in || 0)}
                        <br />
                        (💵{formatCurrency(player.cash_in || 0)} / 📱{formatCurrency((player.total_bought_in || 0) - (player.cash_in || 0))})
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      {isAdmin && (
                        <>
                          <button
                            className="w-8 h-8 rounded-lg bg-[#f5f7f8] flex items-center justify-center cursor-pointer border border-[#e5e9ea] text-sm mr-1"
                            onClick={() => {
                              const newMethod: 'digital' | 'cash' = paymentMethods[player.user_id] === 'cash' ? 'digital' : 'cash';
                              setPaymentMethods(prev => {
                                const next: Record<string, 'digital' | 'cash'> = { ...prev, [player.user_id]: newMethod };
                                if (game) {
                                  savePaymentMethods(game.id, next);
                                }
                                return next;
                              });
                            }}
                            title={paymentMethods[player.user_id] === 'cash' ? 'Pay by Cash' : 'Pay Digitally'}
                          >
                            {paymentMethods[player.user_id] === 'cash' ? '💵' : '📱'}
                          </button>
                          <button
                            className={`rounded-lg px-3 py-2 text-[11px] font-extrabold text-white min-w-[54px] text-center ${
                              isSuccess ? 'bg-[#22c55e]' : (isPending ? 'bg-gray-400' : 'bg-[#006855]')
                            }`}
                            onClick={() => handleBuyIn(player.user_id, 20)}
                            disabled={isPending}
                          >
                            {isPending ? '...' : isSuccess ? '✓' : (paymentMethods[player.user_id] === 'cash' ? '+💵20' : '+$20')}
                          </button>
                          <button
                            className="w-9 h-9 rounded-lg bg-[#f5f7f8] flex items-center justify-center cursor-pointer border border-[#e5e9ea] text-lg text-[#006855] font-semibold"
                            onClick={() => setOpenCustomInput(isOpen ? null : player.user_id)}
                          >
                            {isOpen ? '×' : '+'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isOpen && isAdmin && (
                    <form
                      className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-[#e5e9ea]"
                      onSubmit={(e) => { e.preventDefault(); handleCustomSubmit(player.user_id); }}
                    >
                      <button
                        type="button"
                        className={`w-10 h-10 rounded-lg flex items-center justify-center cursor-pointer border text-base font-bold mr-1.5 ${ (inputPolarity[player.user_id] || 1) === -1 ? 'bg-[#c2410c] text-white border-[#c2410c]' : 'bg-[#006855] text-white border-[#006855]'}`}
                        onClick={() => togglePolarity(player.user_id)}
                        title={(inputPolarity[player.user_id] || 1) === -1 ? 'Switch to add chip' : 'Switch to deduct chip'}
                      >
                        {(inputPolarity[player.user_id] || 1) === 1 ? '+$' : '−$'}
                      </button>
                      <div className="flex-1 relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-[#595c5d] opacity-50">{(inputPolarity[player.user_id] || 1) === -1 ? '-$' : '$'}</span>
                        <input
                          autoFocus
                          type="text"
                          value={customAmounts[player.user_id] || ''}
                          onChange={(e) => setCustomAmounts(prev => ({ ...prev, [player.user_id]: e.target.value }))}
                          placeholder="0.00"
                          className="w-full bg-[#f5f7f8] border border-[#e5e9ea] rounded-lg py-2 px-3 pl-8 text-[15px] font-bold text-[#2c2f30] outline-none"
                        />
                      </div>
                      <button type="submit" className="bg-[#006855] text-white border-none rounded-lg px-4 py-2 text-[10px] font-extrabold uppercase tracking-widest cursor-pointer h-9">Buy In</button>
                    </form>
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Spectators */}
        {spectators.length > 0 && (
          <div className="mb-3 mt-6">
            <div className="text-[12px] font-bold uppercase tracking-wider text-[#595c5d]">Spectators</div>
          </div>
        )}
        {spectators.length > 0 && (
          <div className="flex flex-wrap">
            {spectators.map(player => (
              <div key={player.user_id} className="inline-flex items-center gap-1.5 bg-white rounded-full px-3.5 py-1.5 mr-2 mb-2 border border-[#e5e9ea]">
                <span className="text-[14px]">👓</span>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#2c2f30]">{player.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Spacer for bottom bar */}
        <div style={{ height: 100 }} />
      </div>

      {/* Floating Bottom Bar */}
      {isAdmin && (
        <div className="fixed w-full max-w-[400px] left-1/2 -translate-x-1/2 px-4 py-3 bg-transparent z-10" style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>
          <div className="max-w-[400px] mx-auto flex flex-col items-center gap-3">
            <button
              onClick={handleDeleteActiveGame}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8a8d8e',
                fontSize: 11,
                fontWeight: 400,
                textDecoration: 'underline',
                cursor: 'pointer',
                marginTop: -6,
                paddingBottom: 8,
              }}
            >
              Delete Game
            </button>
          </div>
        </div>
      )}

      {/* End Game Modal */}
      <Modal 
        isOpen={showEndConfirm} 
        onClose={() => setShowEndConfirm(false)} 
        title="End Game"
      >
        <div className="flex flex-col gap-4">
          <div className="bg-status-error/10 rounded-2xl p-4 border border-status-error/10">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-status-error mb-2">⚠️ Lock Buy-ins?</div>
            <div className="text-xs font-semibold text-status-error opacity-80 leading-relaxed">
              Ending this session will lock all current buy-ins. You will proceed to enter final chip counts to calculate settlements.
            </div>
          </div>
          <div className="flex flex-col gap-2">
             <button
               className="bg-[#006855] text-white border-none rounded-2xl py-3.5 px-4 text-xs font-extrabold uppercase tracking-wider cursor-pointer text-center"
               onClick={handleEndGame}
               disabled={ending}
             >

              {ending ? 'Finishing...' : 'End Game and Lock Pot'}
            </button>
            <button
              className="bg-transparent text-text-muted border-none rounded-2xl py-3.5 px-4 text-xs font-extrabold uppercase tracking-wider cursor-pointer text-center"
              onClick={() => setShowEndConfirm(false)}
            >
              Keep Playing
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Player Modal */}
      <AddPlayerModal
        isOpen={showAddPlayerModal}
        onClose={() => setShowAddPlayerModal(false)}
        onAddPlayer={handleAddPlayer}
        availableUsers={users.filter(u => !game.players.some(p => p.user_id === u.id))}
      />

      <LeaveEarlyModal
        isOpen={leaveModalOpen}
        onClose={() => {
          setLeaveModalOpen(false);
          setSelectedLeavePlayer(null);
        }}
        playerName={selectedLeavePlayer ? (users.find(u => u.id === selectedLeavePlayer.user_id)?.name || selectedLeavePlayer.name || 'Player') : ''}
        cashIn={selectedLeavePlayer?.cash_in || 0}
        initialStack={leaveModalMode === 'edit' ? selectedLeavePlayer?.left_stack : undefined}
        initialCash={leaveModalMode === 'edit' ? selectedLeavePlayer?.left_cash : undefined}
        mode={leaveModalMode}
        onConfirm={async (stack, cash) => {
          if (!selectedLeavePlayer || !game || !user?.id) return;
          await markPlayerLeftEarly(game.id, user.id, selectedLeavePlayer.user_id, stack, cash);
          setLeaveModalOpen(false);
          setSelectedLeavePlayer(null);
        }}
      />
    </div>
  );
}
